import React, { createContext, useState, useEffect } from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import styled from 'styled-components'

export const BlogContext = createContext({})

export const BlogProvider = BlogContext.Provider
export const BlogConsumer = BlogContext.Consumer

interface BlogikProps {
  settings: {
    id: string
    postIds?: Array<number>
    limit?: number
    usePagination?: boolean
  }
  children: any
  postType?: string
}

interface ContextProps {
  isSelected?: any
  setSelectedFilters?: any
  decrementOffset?: any
  incrementOffset?: any
  unfilteredPosts?: any
  limit?: any
  offset?: number
  setOffset?: any
}

function Blogik({
  settings: options,
  children,
  postType = 'posts',
}: BlogikProps) {
  const settings = {
    ...{
      id: 'blog',
      postIds: [],
      limit: 4,
      usePagination: false,
    },
    ...options,
  }

  const {
    allWpPost: { edges: postEdges },
    allWpVacature: { edges: vacatureEdges },
    allWpCategory: { edges: categoryEdges },
    allWpProject: { edges: projectEdges },
    allWpProjectCategory: { edges: projectCategoryEdges },
    // eslint-disable-next-line
  } = useStaticQuery<GatsbyTypes.blogikQueryQuery>(graphql`
    query blogikQuery {
      allWpPost(sort: { order: DESC, fields: date }) {
        edges {
          node {
            ...generalPostFragment
          }
        }
      }
      allWpVacature(sort: { order: DESC, fields: date }) {
        edges {
          node {
            ...generalVacatureFragment
          }
        }
      }
      allWpCategory(filter: { name: { nin: "Uncategorized" } }) {
        edges {
          node {
            id
            name
          }
        }
      }
      allWpProject(sort: { order: ASC, fields: menuOrder }) {
        edges {
          node {
            ...generalProjectFragment
          }
        }
      }
      allWpProjectCategory {
        edges {
          node {
            id
            databaseId
            name
            projectCategory: project_category {
              icon {
                localFile {
                  publicURL
                }
              }
            }
          }
        }
      }
    }
  `)

  let postData: any = postEdges
  let categoryData: any = categoryEdges

  if (postType === 'projects') {
    postData = projectEdges
    categoryData = projectCategoryEdges
  }

  if (postType === 'vacatures') {
    postData = vacatureEdges
  }

  const [selectedFilters, setSelectedFilters] = useState<Array<any>>([])
  const [offset, setOffset] = useState<number>(1)

  const setSelectedFiltersLocal = (filter: any) => {
    const newSelectedFilters = [...selectedFilters]

    const filterIndex: number = newSelectedFilters.indexOf(filter)

    if (filterIndex === -1) {
      newSelectedFilters.push(filter)
    } else {
      newSelectedFilters.splice(filterIndex, 1)
    }

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(
        `${settings.id}-filters`,
        JSON.stringify(newSelectedFilters)
      )
    }

    setSelectedFilters(newSelectedFilters)
  }

  const setOffsetLocal = (newOffset: number) => {
    // if (typeof localStorage !== 'undefined') {
    //   localStorage.setItem(`${settings.id}-offset`, newOffset)
    // }

    setOffset(newOffset)
  }

  const incrementOffset = () => {
    let newOffset: number = Number(offset)

    newOffset += 1

    setOffsetLocal(newOffset)
  }

  const decrementOffset = () => {
    let newOffset = Number(offset)

    newOffset -= 1

    setOffsetLocal(newOffset)
  }

  const isSelected = (filter: any) => selectedFilters.indexOf(filter) !== -1

  const filterPosts = (posts: any, { ids }: any) =>
    posts.filter(({ node }: any, index: number) => {
      let response = true

      if (
        settings.limit !== null &&
        (index + 1 > offset * settings.limit ||
          (settings.usePagination && offset > index + 1))
      ) {
        return false
      }

      if (ids.length > 0 && ids.indexOf(node.databaseId) === -1) {
        return false
      }

      if (selectedFilters.length > 0) {
        response = false

        let postCategories: any = node.categories

        if (postType === 'projects') {
          postCategories = node.projectCategories
        }

        postCategories?.nodes?.forEach((category: any) => {
          if (selectedFilters.indexOf(category.id) !== -1) {
            response = true
          }
        })
      }

      return response
    })

  useEffect(() => {
    if (typeof localStorage !== 'undefined') {
      const storageSelectedFilters = localStorage.getItem(
        `${settings.id}-filters`
      )
      // const storageOffset: any = localStorage.getItem(`${settings.id}-offset`)

      if (storageSelectedFilters) {
        setSelectedFilters(JSON.parse(storageSelectedFilters))
      }

      // if (storageOffset) {
      //   setOffset(storageOffset)
      // }
    }
  }, [])

  const posts = filterPosts(postData, {
    ids: settings.postIds,
  })

  const maxPosts =
    settings.postIds.length > 0 ? settings.postIds.length : postData.length

  return (
    <BlogProvider
      value={{
        setSelectedFilters: setSelectedFiltersLocal,
        selectedFilters,
        isSelected,
        setOffset: setOffsetLocal,
        incrementOffset,
        decrementOffset,
        offset,
        limit: settings.limit,
        filterPosts,
        unfilteredPosts: postData,
        posts,
        categories: categoryData,
        hasPosts: posts.length > 0,
        showMoreButton:
          maxPosts > posts.length && offset * settings.limit < maxPosts,
        showLessButton: offset > 1,
      }}
    >
      {children}
    </BlogProvider>
  )
}

const Button = styled.button`
  outline: none;
  padding: 0;

  &:focus {
    outline: none;
  }
`

interface BlogFilterProps {
  id: string
  children: any
  className?: string
}

export function BlogFilter({
  className = '',
  children,
  id,
}: BlogFilterProps) {
  return (
    <BlogConsumer>
      {(context: ContextProps) => (
        <Button
          className={className}
          role="button"
          data-active={context.isSelected(id) ? 1 : 0}
          onClick={() => {
            context.setSelectedFilters(id)
          }}
          tabIndex={0}
        >
          {children}
        </Button>
      )}
    </BlogConsumer>
  )
}

interface BlogButtonProps {
  increment?: boolean
  children: any
  className?: string
}

export function BlogButton({
  className,
  children,
  increment = true,
}: BlogButtonProps) {
  return (
    <BlogConsumer>
      {(context: ContextProps) => (
        <Button
          className={className}
          type="button"
          onClick={() => {
            if (increment) {
              context.incrementOffset()
            } else {
              context.decrementOffset()
            }
          }}
          tabIndex={0}
        >
          {children}
        </Button>
      )}
    </BlogConsumer>
  )
}

interface BlogPaginationProps {
  className?: string
}

export function BlogPagination({ className = '' }: BlogPaginationProps) {
  return (
    <BlogConsumer>
      {(context: ContextProps) => {
        const paginations: number =
          context.unfilteredPosts.length / context.limit
        const pages = []

        for (let i = 0; i < paginations; i += 1) {
          pages.push(i + 1)
        }

        return (
          <div className={className}>
            {pages.map((page: number) => (
              <Button
                key={page}
                role="button"
                data-active={Number(page) === Number(context.offset) ? 1 : 0}
                onClick={() => {
                  context.setOffset(page)
                }}
                tabIndex={0}
              >
                {page}
              </Button>
            ))}
          </div>
        )
      }}
    </BlogConsumer>
  )
}

export default Blogik
